<div class="flex flex-col gap-2xl py-lg">
  <!-- ── Step identity ── -->

  @if (!step().is_initiation) {
    <lib-section-header
      [title]="
        'workflow_configurations_details_screen_accordion_details_title'
          | transloco
      "
      [contentTemplate]="stepIdentity"
      [isAccordion]="true" />

    <ng-template #stepIdentity>
      <!-- Name edit section (section layout matching workflow-step-info-form) -->
      <form [formGroup]="form">
        <div formGroupName="name">
          <lib-dual-lang-input
            [requiredGroup]="true"
            [inputFormControlName]="'name'"
            [id]="'custom_workflow_step_name'"
            type="text"
            name="name"
            [label]="'custom_workflow_step_name_label' | transloco"
            [requiredEn]="false"
            [requiredAr]="false"
            [placeholderEn]="'custom_workflow_step_name_en_label' | transloco"
            [placeholderAr]="
              'custom_workflow_step_name_ar_label' | transloco
            " />
        </div>
        <lib-validation-message
          [lang]="'current_lang' | transloco"
          control="name" />
      </form>

      <!-- ── Field Access ── -->
      <section class="mt-xl">
        @if (fields().length) {
          <!-- Column legend -->
          <div class="flex items-center justify-between mb-xs px-md">
            <span class="text-xs font-medium text-neutral-400">
              {{ 'custom_workflow_field_access_field' | transloco }}
            </span>
            <div class="flex items-center">
              @for (access of accessLevels; track access) {
                <span
                  class="w-15 text-center text-sm font-medium text-neutral-400">
                  {{ 'custom_workflow_field_access_' + access | transloco }}
                </span>
              }
            </div>
          </div>

          <!-- Field rows -->
          <div
            class="rounded-xl border border-neutral-200 overflow-hidden divide-y divide-neutral-100">
            @for (field of fieldRows(); track field.key) {
              <div
                class="flex items-center justify-between px-md py-sm bg-white hover:bg-neutral-50 transition-colors gap-md">
                <span class="text-sm text-neutral-700 min-w-0 flex-1 truncate">
                  {{ field.title }}
                </span>

                <!-- Segmented toggle -->
                <div
                  class="flex items-stretch rounded-lg border border-neutral-200 overflow-hidden flex-shrink-0">
                  @for (
                    access of accessLevels;
                    track access;
                    let last = $last
                  ) {
                    <button
                      type="button"
                      class="w-14.5 h-4 py-xs text-sm font-medium border-none transition-colors"
                      [class.border-e]="!last"
                      [class.border-neutral-200]="!last"
                      [class.bg-primary-500]="
                        accessValue()[field.key] === access &&
                        access === FieldAccess.Edit
                      "
                      [class.bg-success-600]="
                        accessValue()[field.key] === access &&
                        access === FieldAccess.View
                      "
                      [class.bg-neutral-400]="
                        accessValue()[field.key] === access &&
                        access === FieldAccess.Hidden
                      "
                      [class.text-white]="accessValue()[field.key] === access"
                      [class.bg-white]="accessValue()[field.key] !== access"
                      [class.text-neutral-400]="
                        accessValue()[field.key] !== access
                      "
                      [disabled]="readonly()"
                      (click)="
                        fieldAccessForm.controls[field.key].setValue(access)
                      "></button>
                  }
                </div>
              </div>
            }
          </div>
        } @else {
          <div
            class="flex flex-col items-center gap-sm rounded-xl border border-dashed border-neutral-200 py-2xl text-center">
            <div
              class="flex items-center justify-center size-9 rounded-full bg-neutral-100">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#9ca3af"
                stroke-width="1.8"
                stroke-linecap="round">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <path d="M7 8h10M7 12h10M7 16h6" />
              </svg>
            </div>
            <p class="text-sm text-neutral-400">
              {{ 'custom_workflow_field_access_empty' | transloco }}
            </p>
          </div>
        }
      </section>

      <!-- ── Actions ── -->
      @if (!readonly()) {
        <div class="flex justify-end gap-md mt-xl">
          @if (canDelete()) {
            <lib-button
              id="custom_workflow_step_delete"
              [label]="'custom_workflow_delete_step_button' | transloco"
              [btnClass]="'py-sm px-lg text-sm rounded-md text-destructive-700 bg-white font-medium border border-destructive-300'"
              (click)="remove()" />
          }
          <lib-button
            id="custom_workflow_step_save"
            [disabled]="isSaving()"
            [label]="'custom_workflow_save_button' | transloco"
            [btnClass]="'py-sm px-lg text-sm rounded-md text-white bg-primary-500 font-medium border-none'"
            (click)="save()" />
        </div>
      }
    </ng-template>
  } @else {
    <!-- Initiation header (accordion-style, matching workflow-configuration-details) -->
    <div
      class="rounded-lg bg-neutral-50 border border-neutral-200 overflow-hidden">
      <span class="font-semibold text-neutral-900 text-base px-lg py-md">
        {{ 'custom_workflow_initiation_step_label' | transloco }}
      </span>
      <div class="border-t border-neutral-100 px-lg py-sm">
        <p class="text-xs text-neutral-500 leading-relaxed">
          {{ 'custom_workflow_initiation_description' | transloco }}
        </p>
      </div>
    </div>
  }

  <!-- ── Approvers (kept as-is) ── -->
  @if (!step().is_initiation) {
    <section class="pt-xl">
      <lib-workflow-configuration-details
        [embeddedWorkflowId]="embeddedWorkflowId()"
        [embeddedStepId]="step().id"
        (embeddedSaved)="changed.emit()" />
    </section>
  }

  <!-- ── Reflections (every step of a custom workflow, including initiation) ── -->
  @if (hasReflections()) {
    <lib-section-header
      [title]="'reflection_section_title' | transloco"
      [contentTemplate]="stepReflections"
      [isAccordion]="true" />

    <ng-template #stepReflections>
      <lib-reflection-list
        [flowTemplateId]="workflow().id"
        [scope]="Scope.STEP"
        [stepTemplateId]="step().id"
        (changed)="changed.emit()" />
    </ng-template>
  }
</div>
